import React, { useState } from 'react';
import { FileText, Loader2, Sparkles, X } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

const LENGTH_OPTIONS = [
  { key: 'short', label: 'Short', words: '~100 words' },
  { key: 'medium', label: 'Medium', words: '~300 words' },
  { key: 'extended', label: 'Extended', words: '~800 words' },
];

const SummaryPanel = ({ contentId, contentTitle, onClose }) => {
  const [summaryType, setSummaryType] = useState('medium');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const generate = async (type) => {
    if (!contentId) {
      setError('Missing content id');
      return;
    }
    setSummaryType(type);
    setLoading(true);
    setError(null);
    setSummary(null);
    try {
      const response = await authFetch(
        `${BACKEND_URL}/api/content/${contentId}/summary`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ summary_type: type }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Summary generation failed');
      }
      setSummary({
        text: data.summary,
        cached: data.cached,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="bg-slate-800/70 border border-indigo-700/40 rounded-xl p-5 shadow-lg"
      data-testid="summary-panel"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">AI Summary</h3>
            <p className="text-xs text-gray-400">
              {contentTitle ? `Summarize "${contentTitle}"` : 'Summarize this item'}
            </p>
          </div>
        </div>
        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1 h-7 w-7"
            data-testid="summary-close"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {LENGTH_OPTIONS.map((opt) => (
          <Button
            key={opt.key}
            onClick={() => generate(opt.key)}
            disabled={loading}
            variant={summaryType === opt.key ? 'default' : 'outline'}
            size="sm"
            className={
              summaryType === opt.key
                ? 'bg-indigo-600 hover:bg-indigo-700'
                : 'border-slate-600 text-gray-300 hover:bg-slate-700'
            }
            data-testid={`summary-length-${opt.key}`}
          >
            <FileText className="w-3.5 h-3.5 mr-1.5" />
            {opt.label}
            <span className="ml-1 text-xs text-gray-400">({opt.words})</span>
          </Button>
        ))}
      </div>

      <div className="min-h-[100px] bg-slate-900/50 rounded-lg p-4 border border-slate-700/40">
        {loading && (
          <div className="flex items-center gap-2 text-indigo-300" data-testid="summary-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Generating {summaryType} summary…</span>
          </div>
        )}
        {!loading && error && (
          <p className="text-red-400 text-sm" data-testid="summary-error">{error}</p>
        )}
        {!loading && !error && summary && (
          <div data-testid="summary-text">
            <p className="text-xs text-gray-500 mb-2">
              {summary.cached ? 'Retrieved from cache.' : 'Freshly generated.'}
            </p>
            <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">
              {summary.text}
            </p>
          </div>
        )}
        {!loading && !error && !summary && (
          <p className="text-gray-500 text-sm">
            Pick a length above to generate a summary.
          </p>
        )}
      </div>
    </div>
  );
};

export default SummaryPanel;
